import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ContentCard from '../ContentCard';
import DataTable, { Column } from '../DataTable';
import EmptyState from '../EmptyState';
import LoadErrorBox from '../ui/LoadErrorBox';
import StatusBadge from '../ui/StatusBadge';
import ShareRegistryModal from './ShareRegistryModal';
import { useHeaderSearch } from '../../hooks/useHeaderSearch';
import { GIFT_NEW_EVENT, GIFT_SHARE_EVENT, useGiftRegistryBar } from '../../hooks/useGiftRegistryBar';
import { useLoad } from '../../hooks/useLoad';
import { usePageRefresh } from '../../hooks/usePageRefresh';
import { useRole } from '../../hooks/useRole';
import { fetchGiftItems } from '../../services/GiftRegistryService';
import { resolveMediaUrl } from '../../services/uploadService';
import { isApiError } from '../../utils/apiErrors';
import { formatDecimal } from '../../utils/decimal';
import {
  GIFT_KIND_LABELS, GIFT_NEW_PATH, GIFT_PRIORITY_LABELS, giftItemPath, giftMatches, giftRefLabel, reservedSummary,
} from '../../utils/giftRegistry';
import type { GiftItem, GiftPriority } from '../../types/GiftRegistry';
import '../../styles/components/GiftRegistry.css';

const PRIORITY_VARIANT: Record<GiftPriority, 'danger' | 'neutral' | 'info'> = { high: 'danger', normal: 'neutral', low: 'info' };

/**
 * Honorees' view of the registry: every gift and request, who reserved what. List or cards.
 * Filters, the Archived switch, Share and New item live in the Header's bar (GiftRegistryListBar); this screen reads the same store.
 */
const GiftRegistryList: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const search = useHeaderSearch().trim().toLowerCase();
  const { archived, kind, priority, layout } = useGiftRegistryBar();
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    const share = () => setSharing(true);
    const add = () => navigate(GIFT_NEW_PATH);
    window.addEventListener(GIFT_SHARE_EVENT, share);
    window.addEventListener(GIFT_NEW_EVENT, add);
    return () => { window.removeEventListener(GIFT_SHARE_EVENT, share); window.removeEventListener(GIFT_NEW_EVENT, add); };
  }, [navigate]);

  const load = useLoad(() => fetchGiftItems({ view: archived ? 'archived' : 'active' }), [archived]);
  usePageRefresh(() => load.reload(), load.loading);
  const rows = useMemo(() => load.data ?? [], [load.data]);
  const shown = useMemo(
    () => rows.filter(i => (!kind || i.kind === kind) && (!priority || i.priority === priority) && giftMatches(i, search)),
    [rows, kind, priority, search],
  );

  if (load.error && isApiError(load.error) && load.error.status === 403) {
    return (
      <div className="wt-container fade-in">
        <EmptyState icon="lock" title="You do not have access to the gift registry" description="Ask the project owner to change your role if you need it." />
      </div>
    );
  }
  if (load.error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="the gift registry" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>;
  }

  const open = (i: GiftItem) => giftItemPath(i.ref_number);
  const columns: Column<GiftItem>[] = [
    {
      key: 'ref_number', header: 'ID', width: '96px', sortable: true, className: 'rec-id-cell', sortValue: i => i.ref_number,
      render: (_v, i) => <Link className="gr-link" to={open(i)} onClick={e => e.stopPropagation()}>{giftRefLabel(i.ref_number)}</Link>,
    },
    {
      key: 'title', header: 'Item', sortable: true,
      render: (_v, i) => <Link className="gr-link gr-clamp" to={open(i)} onClick={e => e.stopPropagation()}>{i.title}</Link>,
    },
    { key: 'kind', header: 'Type', sortable: true, render: (_v, i) => GIFT_KIND_LABELS[i.kind] },
    {
      key: 'priority', header: 'Priority', sortable: true, sortValue: i => ({ high: 0, normal: 1, low: 2 })[i.priority],
      render: (_v, i) => <StatusBadge variant={PRIORITY_VARIANT[i.priority]}>{GIFT_PRIORITY_LABELS[i.priority]}</StatusBadge>,
    },
    { key: 'quantity_wanted', header: 'Wanted', sortable: true, align: 'right', render: (_v, i) => i.quantity_wanted },
    { key: 'reserved_quantity', header: 'Reserved', sortable: true, render: (_v, i) => reservedSummary(i) },
    {
      key: 'reservations', header: 'Reserved by',
      render: (_v, i) => (i.reservations.length === 0 ? '—' : <span className="gr-clamp">{i.reservations.map(r => r.guest_name).join(', ')}</span>),
    },
    // Money is left out entirely for someone who may not see money.
    ...(canViewFinancials ? [{
      key: 'price', header: 'Price', sortable: true, align: 'right' as const, sortValue: (i: GiftItem) => Number(i.price ?? 0),
      render: (_v: unknown, i: GiftItem) => (i.price === null ? '—' : formatDecimal(i.price, i.currency)),
    }] : []),
  ];

  const filtered = !!kind || !!priority || !!search;
  const emptyTitle = rows.length > 0 || filtered
    ? 'No items match these filters'
    : archived ? 'No archived items' : 'Your list is empty. Add the first gift or request.';

  return (
    <>
      <div className="wt-container fade-in">
        {!load.loading && shown.length === 0 ? (
          <EmptyState
            icon="gift"
            title={emptyTitle}
            description={rows.length === 0 && !archived ? 'Add things you would love, then share one private link with your guests.' : undefined}
            action={canEdit && rows.length === 0 && !archived
              ? <button type="button" className="primary-btn" onClick={() => navigate(GIFT_NEW_PATH)}>New item</button>
              : undefined}
          />
        ) : layout === 'list' ? (
          <DataTable<GiftItem>
            data={shown}
            columns={columns}
            loading={load.loading}
            emptyMessage="No items"
            pagination={{ enabled: true, pageSize: 25 }}
            onRowClick={i => navigate(open(i))}
          />
        ) : load.loading ? (
          <p className="gr-note" role="status">Loading the gift registry...</p>
        ) : (
          <div className="gr-grid">
            {shown.map(i => (
              <ContentCard
                key={i.id}
                className="gr-card"
                image={i.photo_url ? resolveMediaUrl(i.photo_url) : undefined}
                title={`${giftRefLabel(i.ref_number)}  ${i.title}`}
                subtitle={GIFT_KIND_LABELS[i.kind]}
                description={i.description ?? undefined}
                descriptionLines={3}
                badges={[
                  ...(i.priority === 'high' ? [{ label: 'High priority', type: 'danger' as const }] : []),
                  { label: reservedSummary(i), type: i.reserved_quantity >= i.quantity_wanted ? 'success' as const : 'info' as const },
                ]}
                details={[
                  { label: 'Wanted', value: String(i.quantity_wanted) },
                  ...(canViewFinancials && i.price !== null ? [{ label: 'Price', value: formatDecimal(i.price, i.currency) }] : []),
                ]}
                onClick={() => navigate(open(i))}
              />
            ))}
          </div>
        )}
      </div>
      <ShareRegistryModal open={sharing} onClose={() => setSharing(false)} />
    </>
  );
};

export default GiftRegistryList;
